import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  MapPin,
  Ticket,
  Users,
  X,
  BadgeDollarSign,
} from "lucide-react";

export default function EventDetails({ events, merch = [] }) {
  const { eventId } = useParams();
  const event = events.find(
  (e) =>
    e.id === eventId ||
    e._id === eventId ||
    e.customUrl === eventId
);

  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [ticketQuantity, setTicketQuantity] = useState("1");
  const [ticketsSold, setTicketsSold] = useState([]);
  const [promoCode, setPromoCode] = useState("");
  const [selectedTierTitle, setSelectedTierTitle] = useState("");




  const getTierAvailabilityStatus = (tier) => {
    if (tier.isSoldOut) return "Sold Out";

    const percentRemaining = tier.remaining / Number(tier.quantity);

    if (percentRemaining <= 0.5) return "Moving Fast";

    return "Available";
  };


  const ticketTiers = useMemo(() => {
    if (
      event?.ticketTiers &&
      Array.isArray(event.ticketTiers) &&
      event.ticketTiers.length
    ) {
      return event.ticketTiers;
    }

    if (
      event &&
      typeof event.price !== "undefined" &&
      typeof event.numTickets !== "undefined"
    ) {
      return [
        {
          title: "General Admission",
          price: Number(event.price),
          quantity: Number(event.numTickets),
        },
      ];
    }

    return [];
  }, [event]);

  useEffect(() => {
    if (!event) return;

    async function fetchTickets() {
      try {
        const res = await fetch(`/api/ticketsForEvent/${event._id || event.id}`);
        const data = await res.json();
        if (data.success && Array.isArray(data.tickets)) {
          setTicketsSold(data.tickets);
        }
      } catch (err) {
        console.error("Error fetching tickets for event:", err);
      }
    }

    fetchTickets();
  }, [event]);

  useEffect(() => {
    if (!ticketTiers.length) return;

    const currentStillExists = ticketTiers.some(
      (tier) => tier.title === selectedTierTitle
    );
    if (!selectedTierTitle || !currentStillExists) {
      setSelectedTierTitle(ticketTiers[0].title);
    }
  }, [ticketTiers, selectedTierTitle]);

  if (!event) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Event not found
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            The event you’re looking for may no longer exist.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to events
          </Link>
        </div>
      </main>
    );
  }

  const ticketsSoldByTier = ticketTiers.reduce((acc, tier) => {
    acc[tier.title] = ticketsSold.filter(
      (ticket) => (ticket.ticketTierTitle || "General Admission") === tier.title
    ).length;
    return acc;
  }, {});

  const enrichedTiers = ticketTiers.map((tier) => {
    const sold = ticketsSoldByTier[tier.title] || 0;
    const remaining = Math.max(Number(tier.quantity) - sold, 0);
    return {
      ...tier,
      sold,
      remaining,
      isSoldOut: remaining <= 0,
    };
  });

  const selectedTier =
    enrichedTiers.find((tier) => tier.title === selectedTierTitle) ||
    enrichedTiers[0];

  const allTiersSoldOut =
    enrichedTiers.length > 0 && enrichedTiers.every((tier) => tier.isSoldOut);

  const minPrice =
    enrichedTiers.length > 0
      ? Math.min(...enrichedTiers.map((tier) => Number(tier.price)))
      : 0;

  const normalizedPromo = promoCode.toUpperCase();
  const isValidPromo = normalizedPromo === "VET20";

  const quantity = parseInt(ticketQuantity, 10) || 1;
  const baseTotal = (Number(selectedTier?.price) || 0) * quantity;
  const discountAmount = isValidPromo ? baseTotal * 0.2 : 0;
  const finalTotal = baseTotal - discountAmount;

  const formattedDate = new Date(event.dateTime).toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  const totalRemaining = enrichedTiers.reduce(
    (sum, tier) => sum + tier.remaining,
    0
  );


  const linkedMerch = merch.filter((item) => {
    const linkedIds = [
      ...(Array.isArray(item.associatedEvents) ? item.associatedEvents : []),
      ...(item.eventId ? [item.eventId] : []),
    ].filter(Boolean);

    return linkedIds.includes(event._id || event.id) && item.active !== false;
  });

  const handleBuyClick = () => {
    if (!selectedTier || selectedTier.isSoldOut) return;
    setShowCheckoutForm(true);
  };

  const handleCheckout = async () => {
    if (!buyerEmail || !buyerName || quantity < 1 || !selectedTier) {
      alert("Please fill out all fields and select at least 1 ticket.");
      return;
    }

    if (selectedTier.remaining < quantity) {
      alert(`Only ${selectedTier.remaining} ${selectedTier.title} tickets remain.`);
      return;
    }

    try {
      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eventId: event._id || event.id,
          buyerEmail,
          buyerName,
          quantity,
          promoCode: isValidPromo,
          ticketTierTitle: selectedTier.title,
        }),
      });

      const data = await res.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.message || "Failed to create checkout session");
      }
    } catch (err) {
      console.error(err);
      alert("Error starting checkout");
    }
  };

  return (
    <>
      <main className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
          <header className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to events
            </Link>
          </header>

          <div className="grid gap-8 lg:grid-cols-[1.35fr_0.9fr] lg:items-start">
            <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="relative h-[280px] overflow-hidden bg-slate-100 sm:h-[380px] lg:h-[460px]">
                {event.image ? (
                  <img
                    src={event.image}
                    alt={event.name}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80";
                    }}
                  />
                ) : (
                  <img
                    src="https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80"
                    alt={event.name}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-slate-900/10 to-transparent" />

                <div className="absolute left-5 top-5 flex flex-wrap gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold shadow-sm backdrop-blur-sm ${
                      allTiersSoldOut
                        ? "bg-red-600 text-white"
                        : "bg-white/90 text-slate-800"
                    }`}
                  >
                    {allTiersSoldOut ? "Sold Out" : "Tickets Available"}
                  </span>

                  {!allTiersSoldOut && (
                    <span className="inline-flex items-center rounded-full bg-slate-900/70 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                      {totalRemaining <= 0
                        ? "Sold Out"
                        : totalRemaining / enrichedTiers.reduce((sum, t) => sum + Number(t.quantity), 0) <= 0.5
                        ? "Moving Fast"
                        : "Available"}
                    </span>
                  )}
                </div>
              </div>

              <div className="p-6 sm:p-8">
                <div className="max-w-3xl">
                  <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                    {event.name}
                  </h1>

                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <div className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4">
                      <CalendarDays className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Date & Time
                        </p>
                        <p className="mt-1 text-sm text-slate-700">
                          {formattedDate}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4">
                      <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Location
                        </p>
                        <p className="mt-1 text-sm text-slate-700">
                          {event.location}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 border-t border-slate-200 pt-8">
                    <h2 className="text-lg font-semibold text-slate-900">
                      Event details
                    </h2>
                    <p className="mt-3 whitespace-pre-line leading-7 text-slate-600">
                      {event.description?.trim() ||
                        "Join us for an unforgettable event experience. Reserve your tickets today and secure your spot before availability runs out."}
                    </p>
                  </div>

                  {linkedMerch.length > 0 && (
                    <div className="mt-8 border-t border-slate-200 pt-8">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900">
                            Linked merch
                          </h2>
                          <p className="mt-2 text-sm text-slate-500">
                            These items are also being sold alongside this event.
                          </p>
                        </div>
                        <Link
                          to="/merch"
                          className="text-sm font-medium text-slate-600 transition hover:text-slate-900"
                        >
                          Browse all merch
                        </Link>
                      </div>

                      <div className="mt-5 grid gap-4 sm:grid-cols-2">
                        {linkedMerch.map((item) => {
                          const itemId = item._id || item.id;
                          const image =
                            (Array.isArray(item.photos) && item.photos[0]) ||
                            item.imageUrl ||
                            item.image ||
                            "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&w=1200&q=80";

                          return (
                            <Link
                              key={itemId}
                              to={`/merch/${itemId}`}
                              className="group rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 transition hover:border-slate-300 hover:bg-white"
                            >
                              <div className="flex items-start gap-4">
                                <img
                                  src={image}
                                  alt={item.title || item.name}
                                  className="h-20 w-20 rounded-2xl object-cover"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-base font-semibold text-slate-900">
                                    {item.title || item.name}
                                  </p>
                                  <p className="mt-1 line-clamp-2 text-sm text-slate-500">
                                    {item.description || "Merchandise available for this event."}
                                  </p>
                                  <div className="mt-3 flex items-center justify-between">
                                    <span className="text-sm font-semibold text-emerald-700">
                                      ${(Number(item.price) || 0).toFixed(2)}
                                    </span>
                                    <span className="text-sm font-medium text-slate-600 transition group-hover:text-slate-900">
                                      View merch
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <aside className="lg:sticky lg:top-8">
              <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 bg-slate-50/80 p-6">
                  <p className="text-sm font-medium text-slate-500">
                    Ticket pricing
                  </p>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="text-4xl font-semibold tracking-tight text-slate-900">
                      From ${minPrice}
                    </span>
                    <span className="pb-1 text-sm text-slate-500">
                      per ticket
                    </span>
                  </div>
                </div>

                <div className="space-y-5 p-6">
                  <div className="space-y-3">
                    {enrichedTiers.map((tier) => (
                      <button
                        key={tier.title}
                        type="button"
                        onClick={() => setSelectedTierTitle(tier.title)}
                        className={`w-full rounded-2xl border p-4 text-left transition ${
                          selectedTier?.title === tier.title
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-semibold">{tier.title}</p>
                            <p
                              className={`mt-1 text-sm ${
                                selectedTier?.title === tier.title
                                  ? "text-white/75"
                                  : "text-slate-500"
                              }`}
                            >
                              {getTierAvailabilityStatus(tier)}
                            </p>
                          </div>

                          <div className="text-right">
                            <p className="font-semibold">${tier.price}</p>
                            <p
                              className={`mt-1 text-xs ${
                                selectedTier?.title === tier.title
                                  ? "text-white/75"
                                  : "text-slate-500"
                              }`}
                            >
                              each
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Ticket className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-600">Selected</span>
                      </div>
                      <span className="text-sm font-semibold text-slate-900">
                        {selectedTier?.title || "—"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <BadgeDollarSign className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-600">Price</span>
                      </div>
                      <span className="text-sm font-semibold text-slate-900">
                        ${selectedTier?.price ?? 0}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Users className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-600">Remaining</span>
                      </div>
                      <span className="text-sm font-semibold text-slate-900">
                        {selectedTier ? getTierAvailabilityStatus(selectedTier) : "—"}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handleBuyClick}
                    disabled={
                      !selectedTier || selectedTier.isSoldOut || allTiersSoldOut
                    }
                    className={`w-full rounded-2xl px-5 py-4 text-sm font-semibold transition ${
                      !selectedTier || selectedTier.isSoldOut || allTiersSoldOut
                        ? "cursor-not-allowed bg-slate-200 text-slate-500"
                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                    }`}
                  >
                    {!selectedTier || selectedTier.isSoldOut || allTiersSoldOut
                      ? "Sold Out"
                      : `Buy ${selectedTier.title}`}
                  </button>

                  <p className="text-center text-xs leading-5 text-slate-500">
                    Secure checkout. You’ll be redirected to payment after
                    confirming your details.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

 {showCheckoutForm && selectedTier && (
  <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm">
    <div className="flex min-h-screen items-start justify-center overflow-y-auto p-3 sm:items-center sm:p-6">
      <div className="flex h-[calc(100dvh-1.5rem)] w-full max-w-lg flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh]">
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-slate-200 bg-white px-5 py-4 sm:px-8 sm:py-5">
          <div>
            <h3 className="text-xl font-semibold tracking-tight text-slate-900">
              Complete your order
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Enter your details to continue to payment.
            </p>
          </div>

          <button
            onClick={() => setShowCheckoutForm(false)}
            className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close checkout form"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6">
          <div className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Ticket tier
              </label>
              <select
                value={selectedTierTitle}
                onChange={(e) => setSelectedTierTitle(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
              >
                {enrichedTiers.map((tier) => (
                  <option
                    key={tier.title}
                    value={tier.title}
                    disabled={tier.isSoldOut}
                  >
                   {tier.title} — ${tier.price} ({getTierAvailabilityStatus(tier)})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Full name
              </label>
              <input
                type="text"
                value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Email address
              </label>
              <input
                type="email"
                value={buyerEmail}
                onChange={(e) => setBuyerEmail(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                max={selectedTier.remaining}
                value={ticketQuantity}
                onChange={(e) => setTicketQuantity(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
              />
              <p className="mt-2 text-xs text-slate-500">
                {selectedTier ? getTierAvailabilityStatus(selectedTier) : "—"}
              </p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Promo code
              </label>
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                placeholder="Enter promo code"
              />

              {promoCode && (
                <p
                  className={`mt-2 text-sm ${
                    isValidPromo ? "text-emerald-600" : "text-red-600"
                  }`}
                >
                  {isValidPromo
                    ? "Promo applied: 20% off"
                    : "Invalid promo code"}
                </p>
              )}
            </div>

            <div className="rounded-2xl bg-slate-50 p-4">
              <h4 className="text-sm font-semibold text-slate-900">
                Order summary
              </h4>

              <div className="mt-3 space-y-2 text-sm text-slate-600">
                <div className="flex items-center justify-between">
                  <span>Ticket type</span>
                  <span>{selectedTier.title}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span>Price per ticket</span>
                  <span>${Number(selectedTier.price).toFixed(2)}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span>Quantity</span>
                  <span>{quantity}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span>Subtotal</span>
                  <span>${baseTotal.toFixed(2)}</span>
                </div>

                {isValidPromo && (
                  <div className="flex items-center justify-between text-emerald-600">
                    <span>Discount</span>
                    <span>−${discountAmount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex items-center justify-between border-t border-slate-200 pt-3 text-base font-semibold text-slate-900">
                  <span>Total</span>
                  <span>${finalTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-slate-200 bg-white px-5 py-4 sm:px-8 sm:py-5">
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              onClick={() => setShowCheckoutForm(false)}
              className="rounded-2xl bg-slate-100 px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
            >
              Cancel
            </button>

            <button
              onClick={handleCheckout}
              disabled={
                selectedTier.isSoldOut || selectedTier.remaining < quantity
              }
              className={`rounded-2xl px-5 py-3 text-sm font-semibold text-white transition ${
                selectedTier.isSoldOut || selectedTier.remaining < quantity
                  ? "cursor-not-allowed bg-slate-400"
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              Continue to Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}
    </>
  );
}